import React, { useEffect } from 'react';
import { Box, Drawer, Typography, IconButton, Button, CircularProgress } from '@mui/material';
import { Close as CloseIcon, AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import ProductImage from './common/ProductImage';

const fmt = (n) => parseFloat(n || 0).toFixed(2);

/**
 * Bottom-sheet cart review, shared by the customer dashboard shop (NewOrderView)
 * and the public store (PublicStorePage).
 *
 * The two pages keep different cart shapes and primary actions ("Place Order"
 * vs "Proceed to Checkout"), so this component is intentionally dumb: each page
 * normalises its cart into `items` and supplies the inc/dec + primary handlers.
 *
 * @param {boolean}  open
 * @param {function} onClose
 * @param {Array}    items     - [{ id, name, imageUrl, unitPrice, quantity, raw }]
 * @param {function} onInc     - (item) => void
 * @param {function} onDec     - (item) => void
 * @param {number}   total
 * @param {string}   primaryLabel
 * @param {function} onPrimary
 * @param {boolean}  primaryDisabled
 * @param {boolean}  busy      - shows a spinner on the primary button
 * @param {node}     children  - extra content above the primary button (e.g. address row)
 */
export default function CartReviewSheet({
    open, onClose, items, onInc, onDec, total,
    primaryLabel, onPrimary, primaryDisabled = false, busy = false,
    children, deliveryCharge = 0,
}) {
    // Auto-close once the cart is emptied from within the sheet
    useEffect(() => {
        if (open && items.length === 0) onClose();
    }, [open, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            sx={{ zIndex: 1400 }}
            PaperProps={{ sx: {
                borderTopLeftRadius: 16, borderTopRightRadius: 16,
                p: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                maxHeight: '80vh',
            } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">Your Cart</Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ overflowY: 'auto', mx: -0.5, px: 0.5 }}>
                {items.map((it) => (
                    <Box key={it.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                            <ProductImage src={it.imageUrl} name={it.name} fontSize="0.95rem" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap>{it.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                ₹{fmt(it.unitPrice)} × {it.quantity} = <strong>₹{fmt(it.unitPrice * it.quantity)}</strong>
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <IconButton size="small" onClick={() => onDec(it)}>
                                <RemoveCircleOutline fontSize="small" color="primary" />
                            </IconButton>
                            <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 24, textAlign: 'center' }}>
                                {it.quantity}
                            </Typography>
                            <IconButton size="small" onClick={() => onInc(it)}>
                                <AddCircleOutline fontSize="small" color="primary" />
                            </IconButton>
                        </Box>
                    </Box>
                ))}
            </Box>

            {deliveryCharge > 0 ? (
                <Box sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary">Items</Typography>
                        <Typography variant="body2">₹{fmt(total)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                        <Typography variant="body2" color="text.secondary">Delivery</Typography>
                        <Typography variant="body2">₹{fmt(deliveryCharge)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.75, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" fontWeight="bold">Total</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main">₹{fmt(total + deliveryCharge)}</Typography>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25 }}>
                    <Typography variant="subtitle2" fontWeight="bold">Total</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main">₹{fmt(total)}</Typography>
                </Box>
            )}

            {children}

            <Button fullWidth variant="contained" size="large" onClick={onPrimary} disabled={primaryDisabled || busy}>
                {busy ? <CircularProgress size={20} /> : primaryLabel}
            </Button>
        </Drawer>
    );
}
